// AiCreditsModal — buy pre-paid AI credits.
//
// Shown when the user hits an `ai_quota_exceeded` 429 OR clicks the
// "Buy credits" pill in any AI tool. Three pack sizes (small / medium /
// large) at currency-aware prices. Stripe Checkout is hosted, so we
// just POST to /api/billing/ai-credits and redirect on success.
//
// Anonymous users CAN buy — their balance is keyed by salted IP hash
// until they sign up (auth-side migration promotes it to user_id).

import { useState, useCallback, useMemo } from 'react';
import { Sparkles, Loader2, X, AlertTriangle, Check } from 'lucide-react';
import {
  AI_CREDIT_PACKS,
  pickAiCreditsCurrency,
  startAiCreditsCheckout,
  billingErrorMessage,
  formatAmount,
} from '../lib/stripe';
import { useI18n } from '../i18n';
import ValueAnchorHint from './ValueAnchorHint';

// Inline copy in 3 languages. AI features are PT-first and the AI
// dashboards already gate to pt/en/es elsewhere — no need for the
// full 9-lang spread we use on SinglePurchaseCard.
const STR = {
  badge: {
    pt: 'Créditos de IA',  en: 'AI credits',     es: 'Créditos de IA',
  },
  title: {
    pt: 'Recarregue seus créditos de IA',
    en: 'Top up your AI credits',
    es: 'Recarga tus créditos de IA',
  },
  subtitle: {
    pt: 'Use em qualquer ferramenta de IA. 1 crédito = 1 chamada. Sem mensalidade — pague uma vez e use até acabar.',
    en: 'Use across every AI tool. 1 credit = 1 call. No monthly fee — pay once and use until depleted.',
    es: 'Úsalos en cualquier herramienta de IA. 1 crédito = 1 llamada. Sin mensualidad — paga una vez y úsalos hasta agotarlos.',
  },
  packLabel: {
    pt: (n) => `${n} créditos`,
    en: (n) => `${n} credits`,
    es: (n) => `${n} créditos`,
  },
  pricePer: {
    pt: 'por crédito',  en: 'per credit',  es: 'por crédito',
  },
  cta: {
    pt: 'Comprar',  en: 'Buy',  es: 'Comprar',
  },
  loading: {
    pt: 'Abrindo Stripe…',  en: 'Opening Stripe…',  es: 'Abriendo Stripe…',
  },
  perks: {
    pt: [
      'Funciona em todas as ferramentas de IA',
      'Sem assinatura mensal',
      'Saldo nunca expira',
    ],
    en: [
      'Works across every AI tool',
      'No monthly subscription',
      'Balance never expires',
    ],
    es: [
      'Funciona en todas las herramientas de IA',
      'Sin suscripción mensual',
      'El saldo nunca expira',
    ],
  },
  close: { pt: 'Fechar', en: 'Close', es: 'Cerrar' },
  cancel: { pt: 'Cancelar', en: 'Cancel', es: 'Cancel' },
  bestValue: { pt: 'Melhor valor', en: 'Best value', es: 'Mejor valor' },
  popular:   { pt: 'Mais escolhido', en: 'Most popular', es: 'Más elegido' },
};

const tt = (k, lang) => (typeof STR[k]?.[lang] === 'function'
  ? STR[k][lang]
  : STR[k]?.[lang] ?? STR[k]?.en ?? '');

const PACK_ORDER = ['small', 'medium', 'large'];

// Per-pack accent + best-value tag (only on medium / large)
const PACK_ACCENT = {
  small:  { ring: 'border-zinc-200',    glow: '',                                tag: null },
  medium: { ring: 'border-cyan-400',    glow: 'shadow-[0_8px_24px_-12px_rgba(6,182,212,0.5)]', tag: 'popular' },
  large:  { ring: 'border-fuchsia-400', glow: 'shadow-[0_8px_24px_-12px_rgba(217,70,239,0.5)]', tag: 'bestValue' },
};

export default function AiCreditsModal({ open, onClose, lang: langProp, defaultCurrency }) {
  const i18n = useI18n();
  const lang = langProp || i18n?.lang || 'pt';
  const currency = useMemo(
    () => pickAiCreditsCurrency({ lang, currency: defaultCurrency }),
    [lang, defaultCurrency],
  );
  const [busyPack, setBusyPack] = useState(null);
  const [error, setError] = useState(null);

  const handleBuy = useCallback(async (packId) => {
    setError(null);
    setBusyPack(packId);
    try {
      await startAiCreditsCheckout({ pack: packId, currency });
      // startAiCreditsCheckout redirects → unreachable on success
    } catch (e) {
      setError(billingErrorMessage(e));
      setBusyPack(null);
    }
  }, [currency]);

  if (!open) return null;

  const perks = STR.perks[lang] || STR.perks.en;

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/55 px-4 py-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-2xl border border-zinc-200 shadow-2xl w-full max-w-4xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between bg-gradient-to-r from-cyan-600 to-fuchsia-600">
          <div className="flex items-center gap-2 text-white">
            <Sparkles className="w-4 h-4" />
            <span className="text-[10px] font-chunky uppercase tracking-[0.22em]">
              {tt('badge', lang)}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white"
            aria-label={tt('close', lang)}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">
          <h3 className="font-chunky text-2xl text-zinc-950 mb-2">
            {tt('title', lang)}
          </h3>
          <p className="text-sm text-zinc-600 mb-5">
            {tt('subtitle', lang)}
          </p>

          {/* Value anchor — subtle reflective line that reframes the
              price against the cost of doing the same job the slow
              way (designer commission, manual vectorisation, etc.). */}
          <ValueAnchorHint context="ai" tone="dark" className="mb-4" />

          {/* Perks */}
          <ul className="text-sm text-zinc-700 grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5">
            {perks.map((p, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>

          {/* Packs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {PACK_ORDER.map((id) => {
              const pack = AI_CREDIT_PACKS[id];
              const price = pack.prices[currency];
              const accent = PACK_ACCENT[id];
              const isLoading = busyPack === id;
              const perUnit = price / pack.credits;
              return (
                <div
                  key={id}
                  className={`bg-white rounded-xl border-2 p-4 flex flex-col ${accent.ring} ${accent.glow} relative`}
                >
                  {accent.tag && (
                    <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[9px] font-chunky uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-zinc-950 text-cyan-300">
                      {tt(accent.tag, lang)}
                    </div>
                  )}
                  <div className="text-center mb-3">
                    <div className="font-chunky text-4xl text-zinc-950 leading-none">
                      {pack.credits}
                    </div>
                    <div className="text-xs uppercase tracking-wider text-zinc-500 mt-1">
                      {tt('packLabel', lang)(pack.credits).replace(`${pack.credits} `, '')}
                    </div>
                  </div>
                  <div className="text-center mb-3">
                    <div className="font-chunky text-xl text-zinc-900">
                      {formatAmount(price, currency)}
                    </div>
                    <div className="text-[10px] text-zinc-400 mt-0.5">
                      {formatAmount(Math.round(perUnit * 100) / 100, currency)} {tt('pricePer', lang)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleBuy(id)}
                    disabled={!!busyPack}
                    className="mt-auto w-full text-center bg-zinc-950 hover:bg-zinc-800 text-cyan-300 font-chunky uppercase tracking-wider py-2.5 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 text-sm"
                  >
                    {isLoading
                      ? (<><Loader2 size={14} className="animate-spin" /> {tt('loading', lang)}</>)
                      : tt('cta', lang)}
                  </button>
                </div>
              );
            })}
          </div>

          {error && (
            <div className="mt-4 text-rose-700 bg-rose-50 border border-rose-200 rounded p-3 text-sm flex items-start gap-2">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            onClick={onClose}
            className="mt-5 w-full text-xs text-zinc-500 hover:text-zinc-800"
          >
            {tt('cancel', lang)}
          </button>
        </div>
      </div>
    </div>
  );
}
